/**
 * Auth State Machine (XState v5)
 *
 * Centralizes all auth state transitions. The machine guarantees that:
 * - No race conditions between provider events and login flow
 * - Side effects (socket, RevenueCat, backend sync) are triggered by the store layer,
 *   not by the machine — the machine only manages auth state
 *
 * States: idle → restoring → authenticated
 *                authenticating → authenticated
 *                authenticated → loggingOut → idle
 *
 * Backend sync (syncUser) is non-blocking — it runs as a side effect after
 * authentication, same as on session restore. This ensures consistent behavior:
 * a user who logs in with the backend down gets the same experience as one
 * who reloads the app with the backend down.
 */

import { setup, assign, fromPromise } from 'xstate';
import type { AuthProvider, AuthUser, AuthSession, AuthEventType } from './types';

// ─── Machine context & events ────────────────────────────────────

export interface AuthMachineContext {
  user: AuthUser | null;
  session: AuthSession | null;
  error: string | null;
  /** Registration succeeded but email verification is needed before login */
  emailConfirmationRequired: boolean;
}

export type AuthMachineEvent =
  | { type: 'RESTORE' }
  | { type: 'LOGIN'; email: string; password: string }
  | { type: 'REGISTER'; email: string; password: string; name: string }
  | { type: 'SIGNUP_VERIFIED'; user: AuthUser; session: AuthSession }
  | { type: 'LOGOUT' }
  | { type: 'PROVIDER_EVENT'; event: AuthEventType; user: AuthUser | null; session: AuthSession | null }
  | { type: 'CLEAR_ERROR' };

// ─── Services injected at creation time ──────────────────────────

export interface AuthMachineServices {
  provider: AuthProvider;
}

// ─── Authenticate actor result ───────────────────────────────────

interface AuthenticateResult {
  user: AuthUser;
  session: AuthSession;
  emailConfirmationRequired?: never;
}

interface EmailConfirmationResult {
  user: AuthUser;
  session?: never;
  emailConfirmationRequired: true;
}

type AuthenticateOutput = AuthenticateResult | EmailConfirmationResult;

// ─── Machine factory ─────────────────────────────────────────────

export function createAuthMachine(services: AuthMachineServices) {
  return setup({
    types: {
      context: {} as AuthMachineContext,
      events: {} as AuthMachineEvent,
    },
    actors: {
      restoreSession: fromPromise(async () => {
        // Read stored session from the provider (no network call).
        // Backend sync is handled separately by AppNavigator.fetchProfile().
        return services.provider.getSession();
      }),

      authenticate: fromPromise(async ({ input }: { input: { type: 'login'; email: string; password: string } | { type: 'register'; email: string; password: string; name: string } }): Promise<AuthenticateOutput> => {
        if (input.type === 'login') {
          return services.provider.signIn(input.email, input.password);
        }
        const result = await services.provider.signUp(input.email, input.password, {
          name: input.name,
        });
        if (result.emailConfirmationRequired) {
          return { user: result.user, emailConfirmationRequired: true };
        }
        return { user: result.user, session: result.session! };
      }),

      performLogout: fromPromise(async () => {
        await services.provider.signOut();
      }),
    },
    actions: {},
    guards: {},
  }).createMachine({
    id: 'auth',
    initial: 'idle',
    context: {
      user: null,
      session: null,
      error: null,
      emailConfirmationRequired: false,
    },

    states: {
      idle: {
        on: {
          RESTORE: { target: 'restoring' },
          LOGIN: { target: 'authenticating', actions: assign({ emailConfirmationRequired: () => false }) },
          REGISTER: { target: 'authenticating', actions: assign({ emailConfirmationRequired: () => false }) },
          // Typed-code sign-up confirmation (AUTH-013): verifyOtp already
          // succeeded provider-side, so the session is real — sign the user
          // straight in. Handled in idle ONLY: in any other state (double-tap
          // race, already authenticated) the event is silently ignored.
          SIGNUP_VERIFIED: {
            target: 'authenticated',
            actions: assign(({ event }) => ({
              user: event.user,
              session: event.session,
              error: null,
              emailConfirmationRequired: false,
            })),
          },
          CLEAR_ERROR: { actions: assign({ error: () => null }) },
        },
      },

      restoring: {
        invoke: {
          src: 'restoreSession',
          onDone: [
            {
              guard: ({ event }) => event.output !== null,
              target: 'authenticated',
              actions: assign(({ event }) => ({
                user: event.output!.user,
                session: event.output!.session,
                error: null,
              })),
            },
            { target: 'idle' },
          ],
          onError: { target: 'idle' },
        },
      },

      authenticating: {
        invoke: {
          src: 'authenticate',
          input: ({ event }) => {
            if (event.type === 'LOGIN') {
              return { type: 'login' as const, email: event.email, password: event.password };
            }
            if (event.type === 'REGISTER') {
              return { type: 'register' as const, email: event.email, password: event.password, name: event.name };
            }
            throw new Error(`Unexpected event in authenticating: ${event.type}`);
          },
          onDone: [
            {
              // Email confirmation required — registration succeeded but no session
              guard: ({ event }) => event.output.emailConfirmationRequired === true,
              target: 'idle',
              actions: assign({ emailConfirmationRequired: () => true }),
            },
            {
              // Auth provider succeeded — user is authenticated
              target: 'authenticated',
              actions: assign(({ event }) => ({
                user: event.output.user,
                session: event.output.session!,
                error: null,
              })),
            },
          ],
          onError: {
            target: 'idle',
            actions: assign(({ event }) => ({
              error: String((event.error as Error)?.message ?? event.error),
            })),
          },
        },
      },

      authenticated: {
        on: {
          LOGOUT: { target: 'loggingOut' },
          PROVIDER_EVENT: {
            actions: assign(({ context, event }) => ({
              user: event.user ?? context.user,
              session: event.session ?? context.session,
            })),
          },
          CLEAR_ERROR: { actions: assign({ error: () => null }) },
        },
      },

      loggingOut: {
        invoke: {
          src: 'performLogout',
          onDone: {
            target: 'idle',
            actions: assign({ user: () => null, session: () => null, error: () => null }),
          },
          onError: {
            target: 'idle',
            actions: assign({ user: () => null, session: () => null, error: () => null }),
          },
        },
      },
    },
  });
}
